import { createSelector } from 'reselect';
import _ from 'lodash';

import { StoreType } from '@tock/shared/utils/connect';
import { core } from '@tock/coretypes';

const preferenceOptionsSelector = (state: StoreType): core.PreferenceOptions =>
  state.patron.preferenceOptions;

export type PreferenceSelectOptionGroupsByType = {
  [key in keyof core.PreferenceOptions]: SelectOption[][];
};

/**
 * Map preference options by type to groups (exlusive and non-exclusive) of SelectOptions by type
 */
export const preferenceSelectOptionGroupsSelector = createSelector(
  preferenceOptionsSelector,
  (optionsByType): PreferenceSelectOptionGroupsByType => {
    return _.mapValues(optionsByType, (options): SelectOption[][] => {
      const exclusiveOptions: SelectOption[] = [];
      const nonExclusiveOptions: SelectOption[] = [];
      _.forEach(options, (option) => {
        const selectOption = {
          key: option.id || '',
          label: option.text || '',
          value: option.id || '',
        };
        option.isExclusive
          ? exclusiveOptions.push(selectOption)
          : nonExclusiveOptions.push(selectOption);
      });
      return [exclusiveOptions, nonExclusiveOptions].filter(
        (optionGroup) => !_.isEmpty(optionGroup)
      );
    });
  }
);

export type PreferenceOptionMapsByType = {
  [key in keyof core.PreferenceOptions]: Dictionary<core.IPreferenceOption>;
};

/**
 * Create a mapping from id to preference option for quick access
 */
export const preferenceOptionsMapSelector = createSelector(
  preferenceOptionsSelector,
  (preferenceOptionsByType): PreferenceOptionMapsByType => {
    return _.mapValues(preferenceOptionsByType, (preferenceOptions) =>
      _.keyBy(preferenceOptions, 'id')
    );
  }
);

export type PatronPreferenceOptionsByType = {
  [key in keyof core.PreferenceOptions]: core.IPreferenceOption[];
};

/**
 * Map from the patron's preference option ids to the full preference option
 * object with display text
 */
export const patronPreferencesSelector = createSelector(
  (state: StoreType) => state.app.patronProfile,
  preferenceOptionsMapSelector,
  (
    patronProfile,
    { cuisineOptions, dietaryRestrictionOptions, hospitalityOptions }
  ): PatronPreferenceOptionsByType => {
    return {
      cuisineOptions:
        patronProfile?.patronCuisineIds.map((cuisineId) => cuisineOptions[cuisineId]) || [],
      dietaryRestrictionOptions:
        patronProfile?.patronDietaryRestrictionIds.map(
          (restrictionId) => dietaryRestrictionOptions[restrictionId]
        ) || [],
      hospitalityOptions:
        patronProfile?.patronHospitalityPreferenceIds.map(
          (hospitalityId) => hospitalityOptions[hospitalityId]
        ) || [],
    };
  }
);
